/* eslint-disable @typescript-eslint/no-explicit-any */
import ReactCrop, { Crop } from 'react-image-crop';

import { Button, TockDialog } from 'omakase';
import { FileWithPreview } from '@tock/coretypes';
import { Theme, css } from '@emotion/react';
import ImageCropLogic from '@tock/shared/components/modals/ImageCropLogic';
import Spinner from '@tock/shared/components/Spinner';

interface Props {
  file: FileWithPreview;
  aspect?: number;
  onSubmit(file: any, crop: Crop): Promise<any> | void;
  minHeightPixels?: number;
  onClose(): void;
  onShowError(error: string): any;
  submitting: boolean;
  minWidthPixels?: number;
  circularCrop?: boolean;
}

const ImageCropModal = ({
  aspect,
  file,
  submitting,
  circularCrop,
  onSubmit,
  onShowError,
  onClose,
  minHeightPixels = 0,
  minWidthPixels = 0,
}: Props) => {
  const getFooterContent = (handleSubmit: () => void) => (
    <div
      css={({ spacing }: Theme) => css`
        display: flex;
        width: 100%;

        > * + * {
          margin-left: ${spacing(1)}px;
        }
      `}
    >
      <Button size="large" variant="basic" onClick={onClose} fullWidth>
        Cancel
      </Button>
      <Button size="large" variant="primary" onClick={handleSubmit} fullWidth disabled={submitting}>
        {submitting && <Spinner contextClassName="SubmitButton-spinner" />}
        {!submitting && <span>Save</span>}
      </Button>
    </div>
  );

  return (
    <ImageCropLogic
      aspect={aspect}
      file={file}
      onSubmit={onSubmit}
      onShowError={onShowError}
      onClose={onClose}
      minHeightPixels={minHeightPixels}
      minWidthPixels={minWidthPixels}
      render={({ handleSubmit, handleCropChange, handleImageLoaded, crop }) => (
        <TockDialog
          headingConfig={{
            left: {
              configType: 'title',
              title: 'Crop your image',
            },
          }}
          type="fullScreen"
          onClose={onClose}
          data-testid="image-crop-dialog"
          fullHeight
          open={true}
          actionsContent={getFooterContent(handleSubmit)}
          disablePadding
        >
          {file.preview && (
            <div
              className="ImagePreview-image"
              role="application"
              aria-label="Profile image cropper"
            >
              <ReactCrop
                src={file.preview}
                crop={crop}
                onChange={handleCropChange}
                onImageLoaded={handleImageLoaded}
                circularCrop={circularCrop}
                // minWidth prevents a bug when dragging to the top-left direction
                // bug root cause unknown, introduced in commit 869e9d649dc5c5aef3092fc35bf36ebac1904412
                // ReactCrop onChange handler is returning NaN x values and negative y values when dragged top-left
                minWidth={10}
              />
            </div>
          )}
        </TockDialog>
      )}
    />
  );
};

export default ImageCropModal;
